import React, { memo, ReactNode } from "react";
import { Modal as MModal, SxProps, Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import CloseIcon from "@mui/icons-material/Close";
import Text from "../mui/Text";
import IconButton from "../mui/IconButton";
import Box from "../mui/Box";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: "absolute",
    width: 400,
    maxWidth: "95%",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: theme.palette.background.paper,
    border: "1px solid #333",
    boxShadow: theme.shadows[5],
    borderRadius: "8px",
    overflow: "hidden",
  },
  header: {
    padding: theme.spacing(1, 2, 1),
    position: "relative",
  },
  close: {
    position: "absolute",
    right: 2,
    top: 2,
  },
  body: {
    background: "#fff",
    padding: theme.spacing(2),
    maxHeight: "75vh",
    overflow: "auto",
    paddingTop: "0px",
  },
}));

type ModalSize = "xs" | "sm" | "md" | "lg" | "xl";

interface ModalProps {
  children: ReactNode;
  open: boolean;
  size?: ModalSize;
  title?: string;
  onClose?: () => void;
  disableClose?: boolean;
  disableCroseButton?: boolean;
  disableEscapeKeyDown?: boolean;
  sx?: SxProps<Theme>;
  title_xs?: SxProps<Theme>;
}

const Modal: React.FC<ModalProps> = (props) => {
  const classes = useStyles();

  const {
    children,
    open,
    size = "xs",
    title = "",
    onClose,
    disableClose,
    disableCroseButton = false,
    disableEscapeKeyDown,
    sx = {},
    title_xs = {},
  } = props;

  const sizes: Record<ModalSize, number> = {
    xs: 400,
    sm: 600,
    md: 800,
    lg: 1000,
    xl: 1200,
  };

  const handleOnClose = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
    if (reason === "backdropClick") {
      return;
    }
  };

  return (
    <MModal
      open={open}
      onClose={(event, reason) => handleOnClose(event, reason)}
      aria-labelledby="lira-modal-title"
      aria-describedby="lira-modal-description"
      disableEscapeKeyDown={disableEscapeKeyDown}
    >
      <Box
        className={classes.root}
        style={{ width: sizes[size] }}
        sx={{ ...sx }}
      >
        {(title || !disableClose) && (
          <div className={classes.header}>
            {title && (
              <Text
                variant="h5"
                component="h3"
                align="center"
                sx={{
                  fontSize: { xs: "22px", sm: "24px", md: "27px" },
                  minHeight: "30px",
                  ...title_xs,
                }}
              >
                {title}
              </Text>
            )}
            {!disableClose && (
              <IconButton
                sx={{ position: "absolute", top: "14px", color: "#767676" }}
                className={classes.close}
                onClick={onClose}
                disabled={disableCroseButton}
              >
                <CloseIcon />
              </IconButton>
            )}
          </div>
        )}
        <div className={classes.body}>{children}</div>
      </Box>
    </MModal>
  );
};

export default memo(Modal);
